import mapboxgl from "mapbox-gl";
import { fetchTranslation } from "senf-shared";
import i18n from "senf-shared/utils/i18n";

function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): T {
  let inThrottle = false;
  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  }) as T;
}

const useHover = (project: any) => {
  const ideasPopup = new mapboxgl.Popup({
    closeButton: false,
  });

  return (map: mapboxgl.Map) => {
    let lastProcessedPoint: mapboxgl.Point | null = null;
    let lastProcessedTime = 0;
    const THROTTLE_MS = 10;
    const MIN_DISTANCE = 2; // Minimum pixel distance between processed points

    map.on(
      "mousemove",
      throttle((event: mapboxgl.MapMouseEvent) => {
        // Skip if we're too close to the last processed point
        if (lastProcessedPoint) {
          const dx = event.point.x - lastProcessedPoint.x;
          const dy = event.point.y - lastProcessedPoint.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < MIN_DISTANCE) {
            return;
          }
        }

        // Skip if we're processing too frequently
        const now = Date.now();
        if (now - lastProcessedTime < THROTTLE_MS) {
          return;
        }

        lastProcessedPoint = event.point;
        lastProcessedTime = now;

        // check if layers exist
        if (!map?.getLayer("gisData-Point")) {
          return;
        }

        try {
          const features = map.queryRenderedFeatures(event.point, {
            layers: [
              "gisData-Point",
              "gisData-Polygon-outline",
              "gisData-MultiPolygon-outline",
              "gisData-LineString",
              "gisData-MultiLineString",
              "gisData-Polygon-fill",
              "gisData-MultiPolygon-fill",
            ],
          });

          if (!features.length) {
            ideasPopup.remove();
            return;
          }

          const feature = features[0];
          let coordinates: [number, number];

          // Extract coordinates based on geometry type
          switch (feature.geometry.type) {
            case "Point":
              coordinates = feature.geometry.coordinates as [number, number];
              break;
            case "LineString":
            case "Polygon":
            case "MultiPolygon":
              coordinates = map
                .unproject([event.point.x, event.point.y])
                .toArray() as [number, number];
              break;
            default:
              console.error(
                "Unsupported geometry type:",
                feature.geometry.type,
              );
              return;
          }

          if (feature.properties.ideaId) {
            if (
              feature.layer.id === "gisData-MultiPolygon-fill" ||
              feature.layer.id === "gisData-Polygon-fill"
            ) {
              return;
            }
            const title = feature.properties.title;

            const inOriginalLanguage =
              project?.settings?.originalLanguage === i18n.resolvedLanguage;

            if (
              project?.settings?.originalLanguage &&
              !inOriginalLanguage &&
              title
            ) {
              fetchTranslation(project, feature.properties)
                .then((translatedTitle) => {
                  ideasPopup
                    .setLngLat(coordinates)
                    .setText(translatedTitle)
                    .addTo(map);
                })
                .catch((error) => {
                  console.error("Error fetching translation:", error);
                  ideasPopup
                    .setLngLat(coordinates)
                    .setText(feature.properties.title)
                    .addTo(map);
                });
            } else {
              ideasPopup
                .setLngLat(coordinates)
                .setText(feature.properties.title)
                .addTo(map);
            }
          }

          if (
            feature.properties.markerLabelData &&
            (feature.geometry.type === "Polygon" ||
              feature.geometry.type === "MultiPolygon")
          ) {
            try {
              const markerLabelData = JSON.parse(
                feature.properties.markerLabelData,
              );
              if (
                !Array.isArray(markerLabelData) ||
                markerLabelData.length < 2
              ) {
                console.error("Invalid markerLabelData format");
                return;
              }

              const labelText = markerLabelData[0];
              const markerData = markerLabelData[1];

              if (!labelText?.plz_name_long) {
                console.error("Missing plz_name_long in labelText");
                return;
              }

              const parts = labelText.plz_name_long.split(" ");
              const plz = parts[0];
              const cityName = parts.slice(1).join(" ");

              const styledContent = `<div style="display: flex; flex-direction: column; gap: 4px;">
                <span style="color: #8c8884; font-size: 14px;">
                ${plz},
                ${cityName}</span>
                <span style="color: #000000; font-size: 14px;">${markerData}</span>
                </div>`;

              ideasPopup
                .setLngLat(coordinates)
                .setHTML(styledContent)
                .addTo(map);
            } catch (error) {
              console.error("Error processing markerLabelData:", error);
            }
          }

          map.getCanvas().style.cursor = features.length ? "pointer" : "";
        } catch (error) {
          console.error("Error in hover handler:", error);
        }
      }, THROTTLE_MS),
    );
  };
};

export default useHover;
